import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ASSET_COUNT_STATUS_MAP, type AssetCountStatus } from '@goyone/shared';
import { api } from '../../lib/api';
import { dateTime, money } from '../../lib/format';
import { useListState } from '../../lib/hooks';
import { useAuth } from '../../auth/AuthProvider';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { ExportMenu } from '../../components/ExportMenu';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  matchesSearch,
  useLocalFilterTable,
  type FilterDef,
  type LocalAccessors,
} from '../../components/ColumnFilter';
import { exportDay, exportNotes, exportPeriod, type TableExportDoc } from '../../lib/tableExport';

interface CountRow {
  id: string;
  name: string;
  status: AssetCountStatus;
  startedAt: string;
  closedAt: string | null;
  total: number;
  counted: number;
}

interface CountLine {
  id: string;
  assetId: string;
  assetCode: string;
  assetName: string;
  serialNumber: string | null;
  categoryName: string;
  purchaseCost: number;
  expectedCustodianId: string | null;
  expectedCustodianName: string | null;
  expectedLocation: string | null;
  found: boolean | null;
  foundCustodianId: string | null;
  foundCustodianName: string | null;
  foundLocation: string | null;
  note: string | null;
  moved: boolean;
}

interface CountDetail {
  id: string;
  name: string;
  status: AssetCountStatus;
  notes: string | null;
  startedAt: string;
  closedAt: string | null;
  lines: CountLine[];
}

/* ==========================================================================
   فلاتر الجداول — محلّية: الجلسات وأوراقها تصل كاملة
   ========================================================================== */

const COUNT_FILTERS: FilterDef[] = [
  { key: 'name', label: 'الجلسة', kind: 'text', sortable: true },
  { key: 'startedAt', label: 'البدء', kind: 'date', sortable: true },
  { key: 'progress', label: 'التقدّم ٪', kind: 'number', sortable: true },
  {
    key: 'status',
    label: 'الحالة',
    kind: 'enum',
    options: (Object.keys(ASSET_COUNT_STATUS_MAP) as AssetCountStatus[]).map((k) => ({
      value: k,
      label: ASSET_COUNT_STATUS_MAP[k].label,
    })),
  },
];

const COUNT_ACCESSORS: LocalAccessors<CountRow> = {
  progress: (c) => (c.total ? Math.round((c.counted / c.total) * 100) : 0),
};

const FOUND_OPTIONS = [
  { value: 'pending', label: 'لم يُجرَد' },
  { value: 'yes', label: 'موجود' },
  { value: 'no', label: 'مفقود' },
];

const LINE_FILTERS: FilterDef[] = [
  { key: 'asset', label: 'الأصل', kind: 'text', sortable: true },
  { key: 'expected', label: 'المسجَّل', kind: 'text', sortable: true },
  { key: 'found', label: 'موجود', kind: 'enum', options: FOUND_OPTIONS },
  { key: 'foundAt', label: 'وُجد عند', kind: 'text', sortable: true },
  { key: 'location', label: 'الموقع', kind: 'text', sortable: true },
  { key: 'note', label: 'ملاحظة', kind: 'text', noList: true },
];

/*
 * الفلترة على المحفوظ لا على المسودة: من فلتر «لم يُجرَد» ثم أشّر على أصلٍ
 * لا يريده أن يختفي من تحت يده قبل الحفظ — يختفي بعده.
 */
const LINE_ACCESSORS: LocalAccessors<CountLine> = {
  asset: (l) => l.assetName,
  expected: (l) => l.expectedCustodianName ?? 'المخزن',
  found: (l) => (l.found === null ? 'pending' : l.found ? 'yes' : 'no'),
  foundAt: (l) => (l.found ? (l.foundCustodianName ?? 'المخزن') : null),
  location: (l) => l.foundLocation ?? l.expectedLocation,
};

/* ==========================================================================
   قائمة الجلسات
   ========================================================================== */

export function AssetCounts() {
  const [starting, setStarting] = useState(false);

  const counts = useQuery({
    queryKey: ['assetCounts'],
    queryFn: () => api<CountRow[]>('/assets/counts'),
  });

  const open = counts.data?.find((c) => c.status === 'OPEN');

  /* البحث والمدى (على يوم البدء) والفلاتر في الرابط. */
  const { state, patch } = useListState();
  const [search, setSearch] = useState(state.q ?? '');
  const from = state.from;
  const to = state.to;
  const table = useLocalFilterTable({
    rows: counts.data,
    defs: COUNT_FILTERS,
    accessors: COUNT_ACCESSORS,
    state,
    patch,
    search:
      search || from || to
        ? (c: CountRow) => {
            const day = exportDay(c.startedAt) ?? '';
            return (
              matchesSearch(search, c.name) && (!from || day >= from) && (!to || day <= to)
            );
          }
        : undefined,
  });
  const rows = table.rows;

  const buildExport = (): TableExportDoc => ({
    title: 'جلسات جرد الأصول',
    sheets: [
      {
        title: 'جلسات جرد الأصول',
        period: exportPeriod(from, to),
        sections: [
          {
            columns: [
              { key: 'name', label: 'الجلسة' },
              { key: 'startedAt', label: 'البدء', kind: 'date' },
              { key: 'closedAt', label: 'الإغلاق', kind: 'date' },
              { key: 'counted', label: 'جُرد', kind: 'number' },
              { key: 'total', label: 'من', kind: 'number' },
              { key: 'status', label: 'الحالة' },
            ],
            rows: rows.map((c) => ({
              name: c.name,
              startedAt: exportDay(c.startedAt),
              closedAt: exportDay(c.closedAt),
              counted: c.counted,
              total: c.total,
              status: ASSET_COUNT_STATUS_MAP[c.status].label,
            })),
            totals: null,
          },
        ],
        notes: exportNotes({
          q: search,
          conditions: describeFilters(COUNT_FILTERS, table.filters),
          count: rows.length,
          total: rows.length,
          truncated: false,
          unit: 'الجلسات',
        }),
      },
    ],
  });

  return (
    <div className="page">
      <PageHead
        title="جرد الأصول"
        subtitle="مطابقة المسجَّل بالموجود"
        actions={
          <>
            <Link to="/assets" className="btn">
              سجل الأصول
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => setStarting(true)}
              disabled={Boolean(open)}
              title={open ? 'أغلق الجلسة المفتوحة أولًا' : undefined}
            >
              جلسة جرد جديدة
            </button>
          </>
        }
      />

      {open && (
        <div className="alert alert-info mb-4">
          جلسة "{open.name}" مفتوحة — جُرد {open.counted} من {open.total}.{' '}
          <Link to={`/assets/counts/${open.id}`} className="link">
            أكمل الجرد
          </Link>
        </div>
      )}

      {counts.isError && <ErrorState error={counts.error} onRetry={() => counts.refetch()} />}

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث باسم الجلسة"
        />
        <DateRangeFilter from={from} to={to} onChange={(f, t) => patch({ from: f, to: t })} />
        <ExportMenu build={buildExport} disabled={!rows.length} />
        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton {...table.button} />
        </span>
      </div>

      <FilterChips {...table.chips} />

      <FilterScope {...table.scope}>
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>
                  الجلسة
                  <HeaderFilter colKey="name" />
                </th>
                <th>
                  البدء
                  <HeaderFilter colKey="startedAt" />
                </th>
                <th>
                  التقدّم
                  <HeaderFilter colKey="progress" />
                </th>
                <th>
                  الحالة
                  <HeaderFilter colKey="status" />
                </th>
              </tr>
            </thead>

            {counts.isLoading && <TableSkeleton rows={4} cols={4} />}

            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/assets/counts/${c.id}`} className="cell-strong link">
                      {c.name}
                    </Link>
                  </td>
                  <td className="num cell-mute">{dateTime(c.startedAt)}</td>
                  <td className="num cell-mute">
                    {c.counted} / {c.total}
                  </td>
                  <td>
                    <Badge kind={ASSET_COUNT_STATUS_MAP[c.status].kind}>
                      {ASSET_COUNT_STATUS_MAP[c.status].label}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!counts.isLoading && counts.data?.length === 0 && (
          <EmptyState
            title="لا جلسات جرد"
            text="افتح جلسة لتُطابق ما هو مسجَّل بما هو موجود فعلًا."
          />
        )}
        {!!counts.data?.length && rows.length === 0 && (
          <EmptyState title="لا نتائج" text="لا جلسة تطابق هذا البحث أو المدى أو الفلتر." />
        )}
      </div>
      </FilterScope>

      {starting && <StartDialog onClose={() => setStarting(false)} />}
    </div>
  );
}

function StartDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(`جرد ${new Date().toISOString().slice(0, 7)}`);
  const [categoryId, setCategoryId] = useState('');

  const categories = useQuery({
    queryKey: ['assets', 'categories'],
    queryFn: () => api<Array<{ id: string; name: string }>>('/assets/categories'),
  });

  const start = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/assets/counts', {
        method: 'POST',
        body: { name: name.trim(), categoryId: categoryId || undefined },
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['assetCounts'] });
      window.location.href = `/assets/counts/${created.id}`;
    },
  });

  return (
    <Modal
      title="جلسة جرد جديدة"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => start.mutate()}
            disabled={name.trim().length < 2 || start.isPending}
          >
            {start.isPending ? 'جارٍ الفتح' : 'فتح الجلسة'}
          </button>
        </>
      }
    >
      {start.isError && (
        <div className="alert alert-danger mb-4">
          {start.error instanceof Error ? start.error.message : 'تعذّر الفتح.'}
        </div>
      )}

      <div className="field">
        <label className="label req" htmlFor="s-name">
          اسم الجلسة
        </label>
        <input
          id="s-name"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field mt-4">
        <label className="label" htmlFor="s-category">
          حصر الجرد في تصنيف
        </label>
        <select
          id="s-category"
          className="select"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
        >
          <option value="">كل الأصول في الخدمة</option>
          {categories.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/*
        اللقطة تُلتقط عند الفتح: العهدة والموقع وقتها.
        بدونها تُقارَن النتيجة بحالة قد تغيّرت أثناء الجرد، فتظهر فروق ليست فروقًا.
      */}
      <div className="alert alert-info mt-4">
        تُلتقط حالة كل أصل — عهدته وموقعه — لحظة الفتح، وبها تُقارَن نتيجة الجرد.
      </div>
    </Modal>
  );
}

/* ==========================================================================
   ورقة الجرد
   ========================================================================== */

export function AssetCountSheet() {
  const { id = '' } = useParams();
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, Partial<CountLine>>>({});
  const [closing, setClosing] = useState(false);

  const canClose = atLeast('ADMIN');

  const sheet = useQuery({
    queryKey: ['assetCounts', id],
    queryFn: () => api<CountDetail>(`/assets/counts/${id}`),
  });

  const employees = useQuery({
    queryKey: ['employees', { pageSize: 200, status: 'ACTIVE' }],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(
        '/hr/employees?pageSize=200&status=ACTIVE',
      ),
  });

  const save = useMutation({
    mutationFn: () => {
      const lines = Object.entries(draft).map(([assetId, patch]) => {
        const row = sheet.data!.lines.find((l) => l.assetId === assetId)!;
        return {
          assetId,
          found: patch.found ?? row.found ?? false,
          foundCustodianId:
            patch.foundCustodianId !== undefined
              ? patch.foundCustodianId
              : (row.foundCustodianId ?? row.expectedCustodianId),
          foundLocation:
            patch.foundLocation !== undefined
              ? patch.foundLocation
              : (row.foundLocation ?? row.expectedLocation),
          note: patch.note !== undefined ? patch.note : row.note,
        };
      });
      return api<{ saved: number }>(`/assets/counts/${id}/lines`, {
        method: 'POST',
        body: { lines },
      });
    },
    onSuccess: () => {
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['assetCounts'] });
    },
  });

  const s = sheet.data;
  const lines = s?.lines ?? [];
  const pending = lines.filter((l) => (draft[l.assetId]?.found ?? l.found) === null).length;
  const missing = lines.filter((l) => (draft[l.assetId]?.found ?? l.found) === false).length;
  const moved = lines.filter((l) => l.moved).length;
  const dirty = Object.keys(draft).length > 0;
  const closed = s?.status === 'CLOSED';

  const edit = (assetId: string, patch: Partial<CountLine>) =>
    setDraft((d) => ({ ...d, [assetId]: { ...d[assetId], ...patch } }));

  /* البحث والفلاتر تحصر ما يُعرض فقط — العدّادات والحفظ والإغلاق على الورقة كلها. */
  const list = useListState();
  const [search, setSearch] = useState(list.state.q ?? '');
  const table = useLocalFilterTable({
    rows: s?.lines,
    defs: LINE_FILTERS,
    accessors: LINE_ACCESSORS,
    state: list.state,
    patch: list.patch,
    search: search
      ? (l: CountLine) =>
          matchesSearch(
            search,
            l.assetName,
            l.assetCode,
            l.serialNumber,
            l.categoryName,
            l.expectedCustodianName,
            l.foundCustodianName,
            l.expectedLocation,
            l.foundLocation,
            l.note,
          )
      : undefined,
  });

  const buildExport = (): TableExportDoc => ({
    title: s?.name ?? 'ورقة جرد',
    sheets: [
      {
        title: s?.name ?? 'ورقة جرد',
        period: { label: `بدأت ${exportDay(s?.startedAt) ?? ''}` },
        sections: [
          {
            columns: [
              { key: 'code', label: 'الكود' },
              { key: 'name', label: 'الأصل' },
              { key: 'category', label: 'التصنيف' },
              { key: 'expected', label: 'المسجَّل' },
              { key: 'expectedLocation', label: 'الموقع المسجَّل' },
              { key: 'found', label: 'موجود' },
              { key: 'foundAt', label: 'وُجد عند' },
              { key: 'location', label: 'الموقع' },
              { key: 'note', label: 'ملاحظة' },
              { key: 'cost', label: 'تكلفة الشراء', kind: 'money', total: true },
            ],
            rows: table.rows.map((l) => ({
              code: l.assetCode,
              name: l.assetName,
              category: l.categoryName,
              expected: l.expectedCustodianName ?? 'المخزن',
              expectedLocation: l.expectedLocation,
              found: FOUND_OPTIONS.find((o) => o.value === LINE_ACCESSORS.found!(l))?.label,
              foundAt: l.found ? (l.foundCustodianName ?? 'المخزن') : null,
              location: l.foundLocation ?? l.expectedLocation,
              note: l.note,
              cost: l.purchaseCost,
            })),
          },
        ],
        notes: exportNotes({
          q: search,
          conditions: [
            ...describeFilters(LINE_FILTERS, table.filters),
            dirty && 'تعديلاتٌ لم تُحفظ لا تظهر في الملف.',
          ],
          count: table.rows.length,
          total: lines.length,
          truncated: false,
          unit: 'الأصول',
        }),
      },
    ],
  });

  if (sheet.isError) return <ErrorState error={sheet.error} onRetry={() => sheet.refetch()} />;

  return (
    <div className="page">
      <PageHead
        title={s?.name ?? 'ورقة جرد'}
        subtitle={s ? `${lines.length} أصلًا` : undefined}
        actions={
          s && (
            <>
              <Link to="/assets/counts" className="btn">
                رجوع
              </Link>
              {!closed && (
                <>
                  <button
                    className="btn"
                    onClick={() => save.mutate()}
                    disabled={!dirty || save.isPending}
                  >
                    {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
                  </button>
                  {canClose && (
                    <button
                      className="btn btn-primary"
                      onClick={() => setClosing(true)}
                      disabled={pending > 0 || dirty}
                      title={
                        pending > 0
                          ? 'أشِّر على كل أصل أولًا'
                          : dirty
                            ? 'احفظ التعديلات أولًا'
                            : undefined
                      }
                    >
                      إغلاق الجلسة
                    </button>
                  )}
                </>
              )}
            </>
          )
        }
      />

      <div className="stats">
        <Stat label="لم يُجرَد" value={pending} />
        <Stat label="مفقود" value={missing} />
        <Stat label="تغيّر موضعه" value={moved} />
        <Stat
          label="قيمة المفقود"
          value={
            <span className="num neg">
              {money(
                lines
                  .filter((l) => (draft[l.assetId]?.found ?? l.found) === false)
                  .reduce((sum, l) => sum + l.purchaseCost, 0),
              )}
            </span>
          }
        />
      </div>

      {closed && (
        <div className="alert alert-info mb-4">
          الجلسة مُغلقة ولا تقبل تعديلًا. نتائجها محفوظة سجلًّا يُرجَع إليه.
        </div>
      )}

      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            list.patch({ q: v || undefined });
          }}
          placeholder="بحث بالأصل أو الحائز أو الموقع"
        />
        <ExportMenu build={buildExport} disabled={!table.rows.length} />
        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton {...table.button} />
        </span>
      </div>

      <FilterChips {...table.chips} />

      <FilterScope {...table.scope}>
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th>
                  الأصل
                  <HeaderFilter colKey="asset" />
                </th>
                <th>
                  المسجَّل
                  <HeaderFilter colKey="expected" />
                </th>
                <th>
                  موجود
                  <HeaderFilter colKey="found" />
                </th>
                <th>
                  وُجد عند
                  <HeaderFilter colKey="foundAt" />
                </th>
                <th>
                  الموقع
                  <HeaderFilter colKey="location" />
                </th>
                <th>
                  ملاحظة
                  <HeaderFilter colKey="note" />
                </th>
              </tr>
            </thead>

            {sheet.isLoading && <TableSkeleton rows={6} cols={6} />}

            <tbody>
              {table.rows.map((l) => {
                const found = draft[l.assetId]?.found ?? l.found;
                return (
                  <tr key={l.id}>
                    <td>
                      <Link to={`/assets/${l.assetId}`} className="cell-strong link">
                        {l.assetName}
                      </Link>
                      <div className="tiny mute num" dir="ltr">
                        {l.assetCode}
                      </div>
                    </td>
                    <td className="cell-mute">
                      {l.expectedCustodianName ?? 'المخزن'}
                      {l.expectedLocation && (
                        <div className="tiny mute">{l.expectedLocation}</div>
                      )}
                    </td>
                    <td>
                      <select
                        className="select select-sm"
                        value={found === null ? '' : found ? 'yes' : 'no'}
                        onChange={(e) =>
                          edit(l.assetId, {
                            found: e.target.value === '' ? null : e.target.value === 'yes',
                          })
                        }
                        disabled={closed}
                      >
                        <option value="">—</option>
                        <option value="yes">موجود</option>
                        <option value="no">مفقود</option>
                      </select>
                    </td>
                    <td>
                      <select
                        className="select select-sm"
                        value={
                          draft[l.assetId]?.foundCustodianId !== undefined
                            ? (draft[l.assetId]!.foundCustodianId ?? '')
                            : (l.foundCustodianId ?? l.expectedCustodianId ?? '')
                        }
                        onChange={(e) =>
                          edit(l.assetId, { foundCustodianId: e.target.value || null })
                        }
                        disabled={closed || found !== true}
                      >
                        <option value="">المخزن</option>
                        {employees.data?.items.map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="input input-sm"
                        value={
                          draft[l.assetId]?.foundLocation !== undefined
                            ? (draft[l.assetId]!.foundLocation ?? '')
                            : (l.foundLocation ?? l.expectedLocation ?? '')
                        }
                        onChange={(e) =>
                          edit(l.assetId, { foundLocation: e.target.value || null })
                        }
                        disabled={closed || found !== true}
                      />
                    </td>
                    <td>
                      <input
                        className="input input-sm"
                        value={
                          draft[l.assetId]?.note !== undefined
                            ? (draft[l.assetId]!.note ?? '')
                            : (l.note ?? '')
                        }
                        onChange={(e) => edit(l.assetId, { note: e.target.value || null })}
                        disabled={closed}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {lines.length > 0 && table.rows.length === 0 && (
          <EmptyState title="لا نتائج" text="لا أصل يطابق هذا البحث أو الفلتر." />
        )}
      </div>
      </FilterScope>

      {closing && s && (
        <CloseDialog
          count={s}
          missing={missing}
          moved={moved}
          onClose={() => setClosing(false)}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['assetCounts'] });
            setClosing(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * إغلاق الجلسة.
 *
 * التطبيق اختياري ولا يُفعَّل تلقائيًا: المفقود قد يكون في سيارة موظف لا
 * ضائعًا، وتصحيح السجل بمجرد أنه لم يُرَ يوم الجرد يمحو أصلًا موجودًا.
 */
function CloseDialog({
  count,
  missing,
  moved,
  onClose,
  onDone,
}: {
  count: CountDetail;
  missing: number;
  moved: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [applyFindings, setApply] = useState(false);
  const [notes, setNotes] = useState('');

  const close = useMutation({
    mutationFn: () =>
      api(`/assets/counts/${count.id}/close`, {
        method: 'POST',
        body: { applyFindings, notes: notes || null },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title="إغلاق جلسة الجرد"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => close.mutate()}
            disabled={close.isPending}
          >
            {close.isPending ? 'جارٍ الإغلاق' : 'إغلاق'}
          </button>
        </>
      }
    >
      {close.isError && (
        <div className="alert alert-danger mb-4">
          {close.error instanceof Error ? close.error.message : 'تعذّر الإغلاق.'}
        </div>
      )}

      <div className="totals-box">
        <div className="totals-row">
          <span className="mute">مفقود</span>
          <span className={`num ${missing ? 'neg' : ''}`}>{missing}</span>
        </div>
        <div className="totals-row totals-final">
          <span>تغيّر موضعه أو حائزه</span>
          <span className="num">{moved}</span>
        </div>
      </div>

      <label className="check mt-4">
        <input
          type="checkbox"
          checked={applyFindings}
          onChange={(e) => setApply(e.target.checked)}
        />
        <span>تطبيق النتيجة على سجلات الأصول</span>
      </label>

      <div className={`alert mt-3 ${applyFindings ? 'alert-warning' : 'alert-info'}`}>
        {applyFindings
          ? 'ستُحدَّث عهدة الأصول ومواقعها بما وُجد في الجرد، وتُسجَّل الحركة في سجل كل أصل. المفقود لا يُعدَم تلقائيًا — استبعده يدويًا بعد التأكد.'
          : 'تُحفظ النتائج سجلًّا ولا تُمسّ بيانات الأصول. المفقود قد يكون في سيارة موظف لا ضائعًا، فالتصحيح قرار لا نتيجة حسابية.'}
      </div>

      <div className="field mt-4">
        <label className="label" htmlFor="cl-notes">
          ملاحظات الإغلاق
        </label>
        <textarea
          id="cl-notes"
          className="input"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </Modal>
  );
}

